import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Grid from './Grid';
import Keyboard from './Keyboard';
import Confetti from 'react-confetti';
import { useProfile } from '../contexts/ProfileContext';
import UnlimitedGameModal from './games/unlimited/UnlimitedGameModal';
import GameImage from './games/unlimited/GameImage';
import GameControls from './games/unlimited/GameControls';
import RiddlePanel from './games/unlimited/RiddlePanel';
import SignUpPromptModal from './games/shared/SignUpPromptModal';
import ImagePreviewModal from './games/shared/ImagePreviewModal';
import {
  COUNTDOWN_TIME,
  DEFAULT_STATS,
  ERROR_DISPLAY_TIME,
  GUEST_DAILY_FREE_INSPECTS,
  GUEST_DAILY_FREE_ZOOM_OUTS,
  GUEST_DAILY_GAME_LIMIT,
  GUEST_ZOOM_OUT_PREVIEW_MS,
  INITIAL_GAME_STATE,
  MIN_ZOOM,
  WIN_MODAL_DELAY,
  ZOOM_OUT_PREVIEW_MS,
  ZOOM_DECREASE,
} from '../constants/games/unlimitedGame';
import { generateRandomZoomOrigin } from '../utils/zoomPosition';
import {
  consumeGuestDailyPowerup,
  getGuestDailyGameCount,
  getGuestDailyPowerupsRemaining,
  getSavedGame,
  incrementGuestDailyGameCount,
  manageUsedWordImages,
  saveGame,
  clearSavedGame,
} from '../utils/games/unlimitedGameStorage';
import {
  getSavedGameDb,
  saveGameDb,
} from '../utils/games/unlimitedGameStorageDb';
import { useDebouncedCallback } from '../utils/hooks/useDebouncedCallback';
import {
  getPlaceholderImageUrl,
  preloadImage,
} from '../utils/games/unlimitedGameImages';
import { resolveAccessibleImage } from '../utils/games/imageResolver';
import { fetchRiddle } from '../utils/riddleApi';
import { requestPowerup } from '../utils/powerupApi';
import {
  loadRiddleSession,
  saveRiddleSession,
} from '../utils/games/riddleSessionStorage';
import { getLossRewardCoins, getWinRewardCoins } from '../economy/economyCatalog';
import {
  getRiddleNextCost,
  RIDDLE_DIFFICULTY,
} from '../utils/riddlePricing';
import { useActiveTimer } from '../utils/hooks/useActiveTimer';
import { startRound, finishRound } from '../utils/gameRoundApi';
import { localDevLog, localDevWarn } from '../utils/debug/localDevLogger';
import '../styles/gamePolish.css';
import './games/unlimited/UnlimitedMobileLayout.css';


// Helper: returns a promise that rejects if the given promise doesn't resolve within ms.
const timeoutPromise = (ms, promise) => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Timeout'));
    }, ms);
    promise
      .then((res) => {
        clearTimeout(timer);
        resolve(res);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};


const UnlimitedGame = ({ mobileSideBySide = false }) => {
  // State declarations.
  const [gameState, setGameState] = useState({
    ...INITIAL_GAME_STATE,
    loading: true,
  });
  const [modalState, setModalState] = useState({
    showModal: false,
    title: '',
    isWin: false,
    rewardCoins: null,
    shareZoomLevel: null,
    reportRoundId: null,
  });
  const [errorState, setErrorState] = useState({ showError: false, errorMessage: '' });
  const [confettiState, setConfettiState] = useState({ showConfetti: false });
  const [usedLetters, setUsedLetters] = useState(new Set());
  const [usedWordImages, setUsedWordImages] = useState(new Set());
  const [validWords, setValidWords] = useState(new Set());
  const [wordImageMap, setWordImageMap] = useState({}); // Map words to their image URLs
  const [dictionaryLoaded, setDictionaryLoaded] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [disableTransition, setDisableTransition] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [gameId, setGameId] = useState(0);
  const mounted = React.useRef(true);
  const hasInitialized = React.useRef(false);
  const [showDefinition, setShowDefinition] = useState(false);
  const [definition, setDefinition] = useState(null);
  const [isLoadingDefinition, setIsLoadingDefinition] = useState(false);
  const [waitingForFallback, setWaitingForFallback] = useState(false);
  const { user, loading: profileLoading, coins, refreshCoins, profile, setSeasonPoints, setLifetimePoints } = useProfile();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalContext, setAuthModalContext] = useState('powerup');
  const [guestGamesPlayedToday, setGuestGamesPlayedToday] = useState(0);
  const [isSkipping, setIsSkipping] = useState(false);
  const [freeSkipsRemaining, setFreeSkipsRemaining] = useState(0);
  const [freeInspectsRemaining, setFreeInspectsRemaining] = useState(0);
  const [isInspecting, setIsInspecting] = useState(false);
  const [isZoomingOut, setIsZoomingOut] = useState(false);
  const zoomOutTimerRef = React.useRef(null);
  const [hasRevealableLetters, setHasRevealableLetters] = useState(true);
  const [showRiddlePanel, setShowRiddlePanel] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [riddleText, setRiddleText] = useState('');
  const [riddleHistory, setRiddleHistory] = useState([]);
  const [riddleError, setRiddleError] = useState('');
  const [isLoadingRiddle, setIsLoadingRiddle] = useState(false);
  const [riddleUseCount, setRiddleUseCount] = useState(0);
  const [freeZoomOutsRemaining, setFreeZoomOutsRemaining] = useState(0);
  const [freeEasyRiddlesRemaining, setFreeEasyRiddlesRemaining] = useState(0);
  // Keep DB/local payloads bounded (riddle strings can get long).
  const capRiddleHistory = useCallback((arr) => {
    if (!Array.isArray(arr)) return [];
    const MAX_RIDDLES_TO_PERSIST = 20;
    if (arr.length <= MAX_RIDDLES_TO_PERSIST) return arr;
    return arr.slice(arr.length - MAX_RIDDLES_TO_PERSIST);
  }, []);
  const usedWordImagesRef = React.useRef(new Set());
  const rewardGuardRef = React.useRef({ gameId: null, rewarded: false });
  const roundIdRef = React.useRef(null);
  const activeTimer = useActiveTimer();

  const coinsSafe = typeof coins === 'number' ? coins : 0;
  const nextRiddleCostNormal = useMemo(
    () =>
      getRiddleNextCost({
        useCount: riddleUseCount,
        difficulty: RIDDLE_DIFFICULTY.HARD,
        guessRow: gameState.currentRow,
      }),
    [riddleUseCount, gameState.currentRow]
  );
  const nextRiddleCostEasy = useMemo(
    () =>
      getRiddleNextCost({
        useCount: riddleUseCount,
        difficulty: RIDDLE_DIFFICULTY.EASY,
        guessRow: gameState.currentRow,
      }),
    [riddleUseCount, gameState.currentRow]
  );
  const canAffordNormalRiddle = coinsSafe >= nextRiddleCostNormal;
  const canAffordEasyRiddle = freeEasyRiddlesRemaining > 0 || coinsSafe >= nextRiddleCostEasy;
  const guestGameLimitReached =
    !user && guestGamesPlayedToday >= GUEST_DAILY_GAME_LIMIT;

  const openAuthModal = useCallback((context = 'powerup') => {
    setAuthModalContext(context);
    setShowAuthModal(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setShowAuthModal(false);
    setAuthModalContext('powerup');
  }, []);

  const openImagePreview = useCallback((clickedSrc) => {
    if (!clickedSrc) return;
    setPreviewImageUrl(clickedSrc);
  }, []);

  const closeImagePreview = useCallback(() => {
    setPreviewImageUrl('');
  }, []);

  // showErrorMessage helper.
  const showErrorMessage = useCallback((message) => {
    setErrorState({ showError: true, errorMessage: message });
    setTimeout(() => {
      setErrorState((prev) => ({ ...prev, showError: false }));
    }, ERROR_DISPLAY_TIME);
  }, []);

  useEffect(() => {
    usedWordImagesRef.current = usedWordImages;
  }, [usedWordImages]);

  useEffect(() => {
    setPreviewImageUrl('');
  }, [gameState.imageUrl]);

  useEffect(() => {
    hasInitialized.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (profileLoading) return;

    if (user) {
      try {
        const saved = localStorage.getItem('zoomedInStats');
        setStats(saved ? JSON.parse(saved) : DEFAULT_STATS);
      } catch (error) {
        console.error('Error loading stats:', error);
        setStats(DEFAULT_STATS);
      }
    } else {
      setStats(DEFAULT_STATS);
    }
  }, [user, profileLoading]);

  useEffect(() => {
    if (profileLoading) return;
    if (user) {
      setGuestGamesPlayedToday(0);
      return;
    }
    setGuestGamesPlayedToday(getGuestDailyGameCount());
  }, [user, profileLoading]);

  // Load word lists.
  useEffect(() => {
    const loadWordLists = async () => {
      try {
        // Load data from ChosenImages15march.json
        const response = await fetch('/ChosenImages15march.json');
        if (!response.ok) {
          throw new Error('Failed to load ChosenImages15march.json');
        }
        
        const imageData = await response.json();
        localDevLog(`Loaded ${imageData.length} words from ChosenImages15march.json`);
        
        // Create a mapping of words to valid image URLs
        const wordMap = {};
        let validUrlCount = 0;
        
        imageData.forEach(item => {
          const word = item.word?.trim().toLowerCase();
          
          // Only use 5-letter words
          if (!word || word.length !== 5) {
            return;
          }
          
          // Get IDs from both services
          const pixabayIds = Array.isArray(item.pixabay_ids) 
            ? item.pixabay_ids.filter(id => id && !id.includes('PHOTO REQUIRED'))
            : [];
          
          const pexelsIds = Array.isArray(item.pexels_ids)
            ? item.pexels_ids.filter(id => id && !id.includes('PHOTO REQUIRED'))
            : [];
          
          if (pixabayIds.length > 0 || pexelsIds.length > 0) {
            wordMap[word] = { pixabayIds, pexelsIds };
            validUrlCount++;
          }
        });
        
        localDevLog(`Found ${validUrlCount} words with valid image IDs`);
        localDevLog(`Total words in map: ${Object.keys(wordMap).length}`);
        
        // If no words were found, create a fallback map with some words and generic images
        if (Object.keys(wordMap).length === 0) {
          localDevLog('No words found in test.txt, using fallback word set');
          const fallbackWords = ["apple", "beach", "chair", "daisy", "earth"];
          fallbackWords.forEach(word => {
            wordMap[word] = { pixabayIds: [], pexelsIds: [] };
          });
          localDevLog(`Added ${fallbackWords.length} fallback words`);
        }
        
        setWordImageMap(wordMap);
        const wordList = Object.keys(wordMap);

        const dictionaryResponse = await fetch('/wordlistdictionary.txt');
        if (!dictionaryResponse.ok)
          throw new Error('Failed to load dictionary file');
        const dictionaryText = await dictionaryResponse.text();
        const dictionaryWords = dictionaryText
          .split('\n')
          .map((word) => word.trim().toLowerCase())
          .filter((word) => word.length === 5);
        setValidWords(new Set([...wordList, ...dictionaryWords]));
        setDictionaryLoaded(true);
      } catch (error) {
        console.error('Error loading word lists:', error);
      }
    };
    loadWordLists();
  }, []);

  // Initialize first game once dictionary is loaded.
  useEffect(() => {
    mounted.current = true;
    const initialize = async () => {
      if (!mounted.current || hasInitialized.current) return;
      if (profileLoading) return;
      if (!dictionaryLoaded || Object.keys(wordImageMap).length === 0) return;
      hasInitialized.current = true;

      let savedGame = null;

      if (user) {
        savedGame = await getSavedGameDb(user.id);
        const savedUsedWordImages = savedGame?.usedWordImages || [];
        const savedUsedSet = new Set(savedUsedWordImages);
        usedWordImagesRef.current = savedUsedSet;
        setUsedWordImages(savedUsedSet);
      } else {
        savedGame = getSavedGame();
        const clearedSet = new Set();
        usedWordImagesRef.current = clearedSet;
        setUsedWordImages(clearedSet);
      }
      const guestDailyCount = user ? 0 : getGuestDailyGameCount();
      if (!user) {
        setGuestGamesPlayedToday(guestDailyCount);
      }

      if (savedGame) {
        try {
          const parsedGame = savedGame;
          const {
            usedLetters: savedUsedLetters,
            ...restoredState
          } = parsedGame;
          // Ensure the saved game isn't already completed
          if (parsedGame.gameOver) {
            if (!user && guestDailyCount >= GUEST_DAILY_GAME_LIMIT) {
              setGameState((prev) => ({ ...prev, loading: false }));
              setImageLoading(false);
              openAuthModal('guest_limit');
              return;
            }
            await initializeGame(); // Start a new game if previous game was over
          } else {
            let resolvedImageUrl = restoredState.imageUrl;

            if (restoredState.imageSource && restoredState.imageId) {
              resolvedImageUrl = await getImageUrlFromSource(
                restoredState.solution,
                restoredState.imageSource,
                restoredState.imageId
              );
              try {
                await preloadImage(resolvedImageUrl);
              } catch (error) {
                localDevWarn('Failed to preload restored image, using placeholder');
                resolvedImageUrl = getPlaceholderImageUrl(restoredState.solution);
              }
            }

            // Restore saved game state
            setGameState({
              ...restoredState,
              imageUrl: resolvedImageUrl,
              isTransitioning: false,
              isChecking: false,
              loading: false,
            });

            // Restore used letters
            if (savedUsedLetters) {
              setUsedLetters(new Set(savedUsedLetters));
            }

            // Restored rounds need fresh tracking because timer/ref state resets on reload.
            activeTimer.start();
            startRound({ gameType: 'unlimited' }).then((id) => {
              roundIdRef.current = id;
            });

            setGameId((prev) => prev + 1);
            setImageLoading(false);
            localDevLog('Restored saved game with solution:', parsedGame.solution);
          }
        } catch (error) {
          console.error('Error restoring saved game:', error);
          await initializeGame(); // Initialize new game if restoration fails
        }
      } else {
        if (!user && guestDailyCount >= GUEST_DAILY_GAME_LIMIT) {
          setGameState((prev) => ({ ...prev, loading: false }));
          setImageLoading(false);
          openAuthModal('guest_limit');
          return;
        }
        await initializeGame(); // Initialize new game if no saved game
      }
    };

    initialize();
    return () => {
      mounted.current = false;
      if (zoomOutTimerRef.current) {
        clearTimeout(zoomOutTimerRef.current);
        zoomOutTimerRef.current = null;
      }
    };
  }, [dictionaryLoaded, wordImageMap, user, profileLoading, openAuthModal]);

  const { debounced: persistGameDebounced, cancel: cancelPersistGameDebounced } = useDebouncedCallback(
    async ({ userId, gameStateSnapshot, usedLettersSnapshot, usedWordImagesSnapshot }) => {
      if (!gameStateSnapshot?.solution || gameStateSnapshot.loading) return;
      if (userId) {
        await saveGameDb(userId, gameStateSnapshot, usedLettersSnapshot, usedWordImagesSnapshot);
      } else {
        saveGame(gameStateSnapshot, usedLettersSnapshot);
      }
    },
    900
  );

  // Debounced persistence (prevents DB writes on every keystroke).
  useEffect(() => {
    if (!gameState.solution || gameState.loading) {
      // Important: cancel any pending save so we don't write stale state after a reset/skip.
      cancelPersistGameDebounced();
      return;
    }

    persistGameDebounced({
      userId: user?.id || null,
      gameStateSnapshot: gameState,
      usedLettersSnapshot: usedLetters,
      usedWordImagesSnapshot: usedWordImagesRef.current,
    });
  }, [
    gameState,
    usedLetters,
    usedWordImages,
    user?.id,
    persistGameDebounced,
    cancelPersistGameDebounced,
  ]);

  // Modal countdown.
  useEffect(() => {
    if (modalState.showModal) {
      setCountdown(COUNTDOWN_TIME);
    }
  }, [modalState.showModal]);
  useEffect(() => {
    if (modalState.showModal && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [modalState.showModal, countdown]);

  // Get a random word and image that hasn't been used together
  const getRandomWordAndImage = useCallback(() => {
    // Get available words
    const availableWords = Object.keys(wordImageMap);
    if (availableWords.length === 0) {
      console.error('Five letter word list not loaded');
      return { word: 'error', imageSource: null, imageId: null };
    }

    const buildCombinations = (usedSet) => {
      const combinations = [];
      availableWords.forEach((word) => {
        const imageData = wordImageMap[word];

        // Add Pixabay combinations
        if (imageData.pixabayIds && imageData.pixabayIds.length > 0) {
          imageData.pixabayIds.forEach((id) => {
            const key = `${word}-pixabay-${id}`;
            if (!usedSet.has(key)) {
              combinations.push({ word, imageSource: 'pixabay', imageId: id });
            }
          });
        }

        // Add Pexels combinations
        if (imageData.pexelsIds && imageData.pexelsIds.length > 0) {
          imageData.pexelsIds.forEach((id) => {
            const key = `${word}-pexels-${id}`;
            if (!usedSet.has(key)) {
              combinations.push({ word, imageSource: 'pexels', imageId: id });
            }
          });
        }
      });
      return combinations;
    };

    let usedSet = user ? usedWordImagesRef.current : manageUsedWordImages.get();
    let allCombinations = buildCombinations(usedSet);

    // If we've used all combinations, reset
    if (allCombinations.length === 0) {
      if (user) {
        const resetSet = new Set();
        usedWordImagesRef.current = resetSet;
        setUsedWordImages(resetSet);
        usedSet = resetSet;
      } else {
        manageUsedWordImages.reset();
        usedSet = new Set();
      }
      allCombinations = buildCombinations(usedSet);
    }

    if (allCombinations.length === 0) {
      return { word: 'error', imageSource: null, imageId: null };
    }

    // Select a random combination
    const selectedCombination =
      allCombinations[Math.floor(Math.random() * allCombinations.length)];

    // Mark this combination as used
    if (user) {
      const updatedSet = new Set(usedSet);
      updatedSet.add(
        `${selectedCombination.word}-${selectedCombination.imageSource}-${selectedCombination.imageId}`
      );
      usedWordImagesRef.current = updatedSet;
      setUsedWordImages(updatedSet);
    } else {
      manageUsedWordImages.add(
        selectedCombination.word,
        selectedCombination.imageId,
        selectedCombination.imageSource
      );
    }

    localDevLog(
      `Selected combination: word="${selectedCombination.word}", ${selectedCombination.imageSource} ID=${selectedCombination.imageId}`
    );
    return selectedCombination;
  }, [wordImageMap, user]);


  const getImageUrlFromSource = async (word, imageSource, imageId) => {
    // Uses the shared resolver: tries the saved source/ID first, then
    // cross-service fallback, then placeholder — so restoring a game
    // with a now-broken image ID still shows a real photo when possible.
    const result = await resolveAccessibleImage({
      word,
      wordImageMap,
      preferredSource: imageSource,
      preferredId: imageId,
    });
    return result.url;
  };

  const handleImageLoad = () => {
    localDevLog('Image loaded successfully!');
    setImageLoading(false);
    setWaitingForFallback(false);
  };


  // initializeGame: loads a new game.
  const initializeGame = async () => {
    if (!mounted.current) return;
    try {
      setGameState((prev) => ({ ...prev, loading: true }));
      setImageLoading(true);

      // Pick a random word + preferred image ID.
      const { word, imageSource, imageId } = getRandomWordAndImage();

      // Resolve an accessible image (tries preferred → same-service alts →
      // cross-service alts → placeholder). Returns which source/ID actually
      // worked so the saved-game restore path can re-fetch reliably.
      const resolved = await resolveAccessibleImage({
        word,
        wordImageMap,
        preferredSource: imageSource,
        preferredId: imageId,
      });

      let { url: imageUrl } = resolved;
      const actualSource = resolved.imageSource;
      const actualId = resolved.imageId;

      if (!actualSource) {
        // All real images failed; flag fallback so the UI knows.
        setWaitingForFallback(true);
      }

      // Preload so the first paint isn't blank.
      try {
        await preloadImage(imageUrl);
      } catch {
        imageUrl = getPlaceholderImageUrl(word);
      }

      if (mounted.current) {
        setGameState({
          ...INITIAL_GAME_STATE,
          solution: word,
          imageUrl,
          imageSource: actualSource ?? imageSource,
          imageId: actualId ?? imageId,
          zoomOrigin: generateRandomZoomOrigin(),
          loading: false,
        });
        setGameId((prev) => prev + 1);
        setImageLoading(false);

        // Leaderboard: start round tracking (fire-and-forget)
        activeTimer.start();
        startRound({ gameType: 'unlimited' }).then((id) => {
          roundIdRef.current = id;
        });
      }
    } catch (error) {
      if (mounted.current) {
        setGameState((prev) => ({ ...prev, loading: false }));
        setImageLoading(false);
      }
      showErrorMessage(`Failed to load game: ${error.message}`);
    }
  };

  const clearPersistedGame = useCallback(async () => {
    if (!user) {
      clearSavedGame();
    }
  }, [user]);

  // Reset game: loads a new game when "Next Word" is clicked.
  const resetGame = async () => {
    if (!mounted.current) return;
    if (!user && guestGameLimitReached) {
      showErrorMessage(
        `Daily guest limit reached (${GUEST_DAILY_GAME_LIMIT} games). Sign up to keep playing.`
      );
      openAuthModal('guest_limit');
      return;
    }

    // Leaderboard: discard the current round timer
    activeTimer.stop();
    roundIdRef.current = null;

    // Prevent any pending debounced save from writing stale state after reset.
    cancelPersistGameDebounced();
    
    // Clear saved game when explicitly starting a new one
    await clearPersistedGame();
    
    // Reset definition-related state when starting a new game
    setDefinition(null);
    setShowDefinition(false);
    setIsLoadingDefinition(false);
    
    setGameState((prev) => ({ ...prev, loading: true }));
    setImageLoading(true);
    await initializeGame();
    setModalState({
      showModal: false,
      title: '',
      isWin: false,
      rewardCoins: null,
      reportRoundId: null,
    });
    setConfettiState({ showConfetti: false });
    setUsedLetters(new Set());
    setGameId((prev) => prev + 1);
  };

  const handleGameOver = (isWin, finalGuesses) => {
    // Leaderboard: stop timer and record result, capture points awarded
    const activeMs = activeTimer.stop();
    const reportRoundId = roundIdRef.current;
    if (reportRoundId) {
      finishRound({
        roundId: reportRoundId,
        won: isWin,
        guessesUsed: gameState.currentRow + 1,
        activeTimeMs: activeMs,
      }).then((result) => {
        if (result) {
          const updates = {};
          if (result.pointsAwarded) {
            updates.pointsAwarded = result.pointsAwarded;
            updates.firstGameBonus = result.firstGameBonus;
            setSeasonPoints((prev) => (prev ?? 0) + result.pointsAwarded);
            setLifetimePoints((prev) => (prev ?? 0) + result.pointsAwarded);
          }
          // Server is authoritative for coins (daily-cap enforced).
          if (typeof result.coinsAwarded === 'number') {
            updates.rewardCoins = result.coinsAwarded;
          }
          if (Object.keys(updates).length > 0) {
            setModalState((prev) => ({ ...prev, ...updates }));
          }
          // Refresh the coin balance so the UI header stays in sync.
          refreshCoins?.();
        }
      });
      roundIdRef.current = null;
    }

    // Reset any existing definition data
    setDefinition(null);
    setShowDefinition(false);

    // Prevent duplicate awards if something triggers game over twice.
    if (rewardGuardRef.current.gameId !== gameId) {
      rewardGuardRef.current = { gameId, rewarded: false };
    }

    if (isWin) {
      setConfettiState({ showConfetti: true });
    }

    // Client-side estimate for immediate display (server value overrides in .then above)
    const guessesUsed = gameState.currentRow + 1;
    const rewardCoins = isWin
      ? getWinRewardCoins('unlimited', guessesUsed)
      : getLossRewardCoins();

    if (user) {
      if (isWin) {
        const newStats = {
          ...stats,
          guesses: {
            ...stats.guesses,
            [gameState.currentRow + 1]:
              (stats.guesses[gameState.currentRow + 1] || 0) + 1,
          },
          currentStreak: stats.currentStreak + 1,
          maxStreak: Math.max(stats.maxStreak, stats.currentStreak + 1),
        };
        setStats(newStats);
        localStorage.setItem('zoomedInStats', JSON.stringify(newStats));
      } else {
        const newStats = {
          ...stats,
          losses: (stats.losses || 0) + 1,
          currentStreak: 0,
          guesses: { ...stats.guesses },
        };
        setStats(newStats);
        localStorage.setItem('zoomedInStats', JSON.stringify(newStats));
      }

      // Coins are now awarded server-side by game-round handleFinish.
      // No client-side addCoins call needed.
    } else {
      const nextGuestCount = incrementGuestDailyGameCount();
      setGuestGamesPlayedToday(nextGuestCount);
    }
    setModalState({
      showModal: true,
      title: isWin
        ? 'Congrats, You Win!'
        : `Try Again! The word was: ${gameState.solution.toUpperCase()}`,
      isWin,
      rewardCoins,
      // Capture the zoom the player was seeing before we reset it below.
      shareZoomLevel: gameState.zoomLevel,
      shareZoomOrigin: gameState.zoomOrigin,
      reportRoundId,
    });
    setGameState((prev) => ({
      ...prev,
      // Include the final guess so share/copy results have the complete board.
      guesses: finalGuesses || prev.guesses,
      gameOver: true,
      zoomLevel: MIN_ZOOM,
      isChecking: false,
    }));
  };

  const onKeyPress = useCallback(
    (key) => {
      if (gameState.gameOver) return;
      if (previewImageUrl) return;
      if (key === 'ENTER') {
        if (gameState.currentGuess.length !== 5) {
          showErrorMessage('Word must be 5 letters');
          return;
        }
        const guessLower = gameState.currentGuess.toLowerCase();
        if (!validWords.has(guessLower)) {
          showErrorMessage('Not in word list');
          return;
        }
        const newGuesses = [...gameState.guesses];
        newGuesses[gameState.currentRow] = gameState.currentGuess;
        const newUsedLetters = new Set(usedLetters);
        const currentGuess = gameState.currentGuess.toUpperCase();
        const solution = gameState.solution.toUpperCase();
        const remainingLetters = {};
        [...solution].forEach((letter) => {
          remainingLetters[letter] = (remainingLetters[letter] || 0) + 1;
        });
        [...currentGuess].forEach((letter, i) => {
          if (solution[i] === letter) {
            newUsedLetters.add(`${letter}-correct`);
            remainingLetters[letter]--;
          }
        });
        [...currentGuess].forEach((letter, i) => {
          if (solution[i] !== letter) {
            if (remainingLetters[letter] > 0) {
              newUsedLetters.add(`${letter}-present`);
              remainingLetters[letter]--;
            } else {
              newUsedLetters.add(`${letter}-absent`);
            }
          }
        });
        setUsedLetters(newUsedLetters);
        if (gameState.currentGuess.toLowerCase() === gameState.solution.toLowerCase()) {
          handleGameOver(true, newGuesses);
        } else {
          if (gameState.currentRow >= gameState.guesses.length - 1) {
            handleGameOver(false, newGuesses);
          } else {
            const newZoomLevel =
              gameState.currentRow === 5
                ? MIN_ZOOM
                : Math.max(MIN_ZOOM, gameState.zoomLevel - ZOOM_DECREASE);
            setGameState((prev) => ({
              ...prev,
              guesses: newGuesses,
              currentGuess: '',
              currentRow: prev.currentRow + 1,
              zoomLevel: newZoomLevel,
              isChecking: true,
            }));
            setTimeout(() => {
              setGameState((prev) => ({ ...prev, isChecking: false }));
            }, WIN_MODAL_DELAY);
          }
        }
      } else if (key === '⌫') {
        setGameState((prev) => ({
          ...prev,
          currentGuess: prev.currentGuess.slice(0, -1),
        }));
      } else if (gameState.currentGuess.length < 5 && /^[A-Z]$/.test(key)) {
        setGameState((prev) => ({
          ...prev,
          currentGuess: prev.currentGuess + key,
        }));
      }
    },
    [gameState, previewImageUrl, showErrorMessage, usedLetters, validWords]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        onKeyPress('ENTER');
      } else if (e.key === 'Backspace') {
        onKeyPress('⌫');
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        onKeyPress(e.key.toUpperCase());
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onKeyPress]);

  // Function to fetch word definition
  const fetchDefinition = async (word) => {
    setIsLoadingDefinition(true);
    try {
      localDevLog(`Fetching definition for: ${word}`);
      const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word.toLowerCase()}`);
      if (!response.ok) {
        throw new Error('Definition not found');
      }
      const data = await response.json();
      setDefinition(data);
      localDevLog(`Definition loaded for: ${word}`);
    } catch (error) {
      console.error('Error fetching definition:', error);
      setDefinition(null);
    } finally {
      setIsLoadingDefinition(false);
    }
  };

  // Function to toggle definition
  const toggleDefinition = () => {
    if (!showDefinition) {
      // Always fetch a fresh definition when opening
      fetchDefinition(gameState.solution);
    }
    setShowDefinition(!showDefinition);
  };

  useEffect(() => {
    if (!gameState.solution) return;
    // New word => new riddle session UI.
    // Keep this scoped to `solution` changes so saving riddleSession does not close the panel.
    setShowRiddlePanel(false);
    setRiddleError('');
    setIsLoadingRiddle(false);
  }, [gameState.solution]);

  useEffect(() => {
    if (!gameState.solution) return;

    if (!user?.id) {
      setRiddleText('');
      setRiddleHistory([]);
      setRiddleUseCount(0);
      return;
    }

    const solutionLower = String(gameState.solution || '').toLowerCase();
    const sessionFromDbState = gameState?.riddleSession;
    const hasDbSessionForWord =
      sessionFromDbState &&
      String(sessionFromDbState.word || '').toLowerCase() === solutionLower &&
      Array.isArray(sessionFromDbState.riddles);

    // Prefer DB-backed session so it survives navigation + multi-device.
    // Fall back to localStorage for older sessions / guests.
    const saved = hasDbSessionForWord
      ? {
          useCount: Number.isFinite(sessionFromDbState.useCount) ? sessionFromDbState.useCount : 0,
          riddles: capRiddleHistory(sessionFromDbState.riddles).filter(Boolean),
        }
      : loadRiddleSession({ userId: user.id, word: gameState.solution });

    const nextRiddles = capRiddleHistory(saved.riddles || []);
    const nextUseCount = Number.isFinite(saved.useCount) ? saved.useCount : 0;

    setRiddleHistory(nextRiddles);
    setRiddleUseCount(nextUseCount);
    setRiddleText(nextRiddles?.[nextRiddles.length - 1] || '');

    // If we loaded from localStorage, also mirror into DB-persisted game state.
    if (!hasDbSessionForWord) {
      setGameState((prev) => ({
        ...prev,
        riddleSession: {
          word: prev.solution,
          useCount: nextUseCount,
          riddles: nextRiddles,
        },
      }));
    }
  }, [capRiddleHistory, gameState.riddleSession, gameState.solution, user?.id]);

  const handleRiddlePanelToggle = () => {
    if (gameState.loading || gameState.gameOver) return;
    setShowRiddlePanel((prev) => !prev);
  };

  const revealRiddleWithDifficulty = useCallback(async (difficulty) => {
    if (gameState.loading || gameState.gameOver || isLoadingRiddle) {
      return;
    }
    if (!user) {
      openAuthModal();
      return;
    }
    const canAfford =
      difficulty === RIDDLE_DIFFICULTY.EASY ? canAffordEasyRiddle : canAffordNormalRiddle;
    if (!canAfford) {
      setRiddleError('Not enough coins for the next riddle.');
      showErrorMessage('Not enough coins for a riddle!');
      return;
    }

    setIsLoadingRiddle(true);
    setRiddleError('');

    try {
      const data = await fetchRiddle(gameState.solution, {
        difficulty,
        guessRow: gameState.currentRow,
      });
      const newHistory = capRiddleHistory([...riddleHistory, data.riddle]);
      const newCount = riddleUseCount + 1;

      setRiddleText(data.riddle);
      setRiddleHistory(newHistory);
      setRiddleUseCount(newCount);
      if (difficulty === RIDDLE_DIFFICULTY.EASY) {
        setFreeEasyRiddlesRemaining(data?.freeEasyRiddlesRemaining ?? freeEasyRiddlesRemaining);
      }
      saveRiddleSession({
        userId: user.id,
        word: gameState.solution,
        useCount: newCount,
        riddles: newHistory,
      });

      // Also store in DB-persisted game state (so riddles survive navigation + multi-device).
      setGameState((prev) => ({
        ...prev,
        riddleSession: {
          word: prev.solution,
          useCount: newCount,
          riddles: newHistory,
        },
      }));
      await refreshCoins();
    } catch (error) {
      console.error('Error generating riddle:', error);
      if (error.code === 'NOT_ENOUGH_COINS') {
        showErrorMessage('Not enough coins for a riddle!');
        setRiddleError('Not enough coins for the next riddle.');
      } else if (error.code === 'UNAUTHORIZED') {
        openAuthModal();
      } else {
        setRiddleError('Failed to generate a riddle. Try again.');
      }
    } finally {
      setIsLoadingRiddle(false);
    }
  }, [
    gameState.gameOver,
    gameState.loading,
    gameState.solution,
    capRiddleHistory,
    isLoadingRiddle,
    openAuthModal,
    refreshCoins,
    showErrorMessage,
    user,
    canAffordEasyRiddle,
    canAffordNormalRiddle,
    riddleHistory,
    riddleUseCount,
  ]);

  const handleRevealNormalRiddle = useCallback(
    () => revealRiddleWithDifficulty(RIDDLE_DIFFICULTY.HARD),
    [revealRiddleWithDifficulty]
  );
  const handleRevealEasyRiddle = useCallback(
    () => revealRiddleWithDifficulty(RIDDLE_DIFFICULTY.EASY),
    [revealRiddleWithDifficulty]
  );

  // Add a skip handler function
  const handleSkip = useCallback(async () => {
    if (gameState.loading || gameState.gameOver || isSkipping) return;
    if (!user) {
      openAuthModal();
      return;
    }

    // Leaderboard: discard the current round timer
    activeTimer.stop();
    roundIdRef.current = null;

    // Prevent any pending debounced save from writing stale state after skip.
    cancelPersistGameDebounced();
    
    setIsSkipping(true);
    
    try {
      const data = await requestPowerup({
        type: 'skip',
        word: gameState.solution,
        gameType: 'unlimited',
      });
      setFreeSkipsRemaining(data.freeSkipsRemaining ?? 0);
      setFreeInspectsRemaining(data.freeInspectsRemaining ?? freeInspectsRemaining);
      await refreshCoins();

      if (data.success) {
        // Clear the current game from persistence
        await clearPersistedGame();
        
        // Reset definition-related state
        setDefinition(null);
        setShowDefinition(false);
        setIsLoadingDefinition(false);
        
        setGameState((prev) => ({ ...prev, loading: true }));
        setImageLoading(true);
        
        // Get a new word 
        await initializeGame();
        
        // Reset other states
        setModalState({
          showModal: false,
          title: '',
          isWin: false,
          reportRoundId: null,
        });
        setConfettiState({ showConfetti: false });
        setUsedLetters(new Set());
        setGameId((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error during skip:', error);
      if (error.code === 'NOT_ENOUGH_COINS') {
        showErrorMessage('Not enough coins to skip!');
      } else if (error.code === 'UNAUTHORIZED') {
        openAuthModal();
      } else {
        showErrorMessage('Failed to skip to next word');
      }
    } finally {
      setIsSkipping(false);
    }
  }, [
    gameState.loading,
    gameState.gameOver,
    isSkipping,
    user,
    clearPersistedGame,
    showErrorMessage,
    openAuthModal,
    refreshCoins,
    gameState.solution,
    freeInspectsRemaining,
  ]);

  useEffect(() => {
    if (profileLoading) return;
    if (!user) {
      const guestPowerupsRemaining = getGuestDailyPowerupsRemaining();
      setFreeSkipsRemaining(0);
      setFreeInspectsRemaining(
        guestPowerupsRemaining.inspectRemaining ?? GUEST_DAILY_FREE_INSPECTS
      );
      setFreeZoomOutsRemaining(
        guestPowerupsRemaining.zoomOutRemaining ?? GUEST_DAILY_FREE_ZOOM_OUTS
      );
      setFreeEasyRiddlesRemaining(0);
      return;
    }

    if (profile) {
      setFreeSkipsRemaining(profile.free_skips_remaining ?? 0);
      setFreeInspectsRemaining(profile.free_inspects_remaining ?? 0);
      setFreeZoomOutsRemaining(profile.free_zoom_outs_remaining ?? 0);
      setFreeEasyRiddlesRemaining(profile.free_easy_riddles_remaining ?? 0);
    }
  }, [user, profile, profileLoading]);

  // Add this function to check for revealable letters
  const checkForRevealableLetters = useCallback(() => {
    const allLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const usedLettersArray = Array.from(usedLetters).map(status => status.split('-')[0]);
    const unusedLetters = allLetters.filter(letter => !usedLettersArray.includes(letter));
    const solutionLetters = gameState.solution.toUpperCase().split("");
    const absentLetters = unusedLetters.filter(letter => !solutionLetters.includes(letter));
    
    return absentLetters.length > 0;
  }, [usedLetters, gameState.solution]);

  // Call this whenever usedLetters changes
  useEffect(() => {
    setHasRevealableLetters(checkForRevealableLetters());
  }, [usedLetters, checkForRevealableLetters]);

  const revealAbsentLetters = useCallback(() => {
    const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const usedLettersArray = Array.from(usedLetters).map((status) => status.split('-')[0]);
    const unusedLetters = allLetters.filter((letter) => !usedLettersArray.includes(letter));
    const solutionLetters = gameState.solution.toUpperCase().split('');
    const absentLetters = unusedLetters.filter((letter) => !solutionLetters.includes(letter));

    if (absentLetters.length === 0) {
      showErrorMessage('No more absent letters to reveal!');
      return false;
    }

    const numToReveal = Math.min(3, absentLetters.length);
    const shuffled = [...absentLetters].sort(() => 0.5 - Math.random());
    const lettersToReveal = shuffled.slice(0, numToReveal);
    const newUsedLetters = new Set(usedLetters);
    lettersToReveal.forEach((letter) => {
      newUsedLetters.add(`${letter}-absent`);
    });
    setUsedLetters(newUsedLetters);
    showErrorMessage(`Revealed absent letters: ${lettersToReveal.join(', ')}`);
    return true;
  }, [gameState.solution, showErrorMessage, usedLetters]);

  // Add the handleInspect function
  const handleInspect = useCallback(async () => {
    if (gameState.loading || gameState.gameOver || isInspecting) return;

    if (!checkForRevealableLetters()) {
      showErrorMessage('No more absent letters to reveal!');
      return;
    }

    if (!user && freeInspectsRemaining <= 0) {
      openAuthModal('guest_powerup_limit');
      return;
    }

    setIsInspecting(true);

    try {
      if (!user) {
        const didReveal = revealAbsentLetters();
        if (!didReveal) return;

        const remaining = consumeGuestDailyPowerup('inspect');
        setFreeInspectsRemaining(remaining.inspectRemaining ?? 0);
        setFreeZoomOutsRemaining((prev) => remaining.zoomOutRemaining ?? prev);
        return;
      }

      const data = await requestPowerup({
        type: 'inspect',
        word: gameState.solution,
        gameType: 'unlimited',
      });
      setFreeSkipsRemaining(data.freeSkipsRemaining ?? freeSkipsRemaining);
      setFreeInspectsRemaining(data.freeInspectsRemaining ?? 0);
      await refreshCoins();

      if (data.success) {
        revealAbsentLetters();
      }
    } catch (error) {
      console.error('Error during inspect:', error);
      if (error.code === 'NOT_ENOUGH_COINS') {
        showErrorMessage('Not enough coins to use Inspect!');
      } else if (error.code === 'UNAUTHORIZED') {
        openAuthModal();
      } else {
        showErrorMessage('Failed to reveal letters');
      }
    } finally {
      setIsInspecting(false);
    }
  }, [
    gameState.solution,
    gameState.loading,
    gameState.gameOver,
    isInspecting,
    checkForRevealableLetters,
    freeInspectsRemaining,
    showErrorMessage,
    user,
    openAuthModal,
    refreshCoins,
    freeSkipsRemaining,
    revealAbsentLetters,
  ]);

  const triggerZoomOutPreview = useCallback((durationMs = ZOOM_OUT_PREVIEW_MS) => {
    if (zoomOutTimerRef.current) {
      clearTimeout(zoomOutTimerRef.current);
      zoomOutTimerRef.current = null;
    }

    zoomOutTimerRef.current = setTimeout(() => {
      zoomOutTimerRef.current = null;
      if (mounted.current) {
        setIsZoomingOut(false);
      }
    }, durationMs);
  }, []);

  const handleZoomOut = useCallback(async () => {
    if (gameState.loading || gameState.gameOver || isZoomingOut) return;
    if (!user && freeZoomOutsRemaining <= 0) {
      openAuthModal('guest_powerup_limit');
      return;
    }

    setIsZoomingOut(true);

    try {
      if (!user) {
        const remaining = consumeGuestDailyPowerup('zoom_out');
        setFreeZoomOutsRemaining(remaining.zoomOutRemaining ?? 0);
        setFreeInspectsRemaining((prev) => remaining.inspectRemaining ?? prev);
        triggerZoomOutPreview(GUEST_ZOOM_OUT_PREVIEW_MS);
        return;
      }

      const data = await requestPowerup({
        type: 'zoom_out',
        word: gameState.solution,
        gameType: 'unlimited',
      });
      setFreeZoomOutsRemaining(data.freeZoomOutsRemaining ?? freeZoomOutsRemaining);
      await refreshCoins();

      if (!data?.success) {
        setIsZoomingOut(false);
        return;
      }

      triggerZoomOutPreview();
    } catch (error) {
      console.error('Error during zoom out:', error);
      if (error.code === 'NOT_ENOUGH_COINS') {
        showErrorMessage('Not enough coins to Zoom Out!');
      } else if (error.code === 'UNAUTHORIZED') {
        openAuthModal();
      } else {
        showErrorMessage('Failed to use Zoom Out');
      }
      setIsZoomingOut(false);
    }
  }, [
    gameState.loading,
    gameState.gameOver,
    isZoomingOut,
    user,
    freeZoomOutsRemaining,
    openAuthModal,
    refreshCoins,
    gameState.solution,
    showErrorMessage,
    triggerZoomOutPreview,
  ]);

  const handleInspectBlocked = useCallback(() => {
    if (!user && freeInspectsRemaining <= 0) {
      openAuthModal('guest_powerup_limit');
    }
  }, [freeInspectsRemaining, openAuthModal, user]);

  const handleZoomOutBlocked = useCallback(() => {
    if (!user && freeZoomOutsRemaining <= 0) {
      openAuthModal('guest_powerup_limit');
    }
  }, [freeZoomOutsRemaining, openAuthModal, user]);

  const displayedZoomLevel = isZoomingOut ? MIN_ZOOM : gameState.zoomLevel;

  return (
    <div
      className={`game zi-game-shell ${mobileSideBySide ? 'unlimited-game--split' : ''}`.trim()}
    >
      {errorState.showError && (
        <div className="error-popup">{errorState.errorMessage}</div>
      )}
      {confettiState.showConfetti && (
        <Confetti width={window.innerWidth} height={window.innerHeight} />
      )}
      {modalState.showModal && (
        <div className="modal-overlay">
          <UnlimitedGameModal
            modalState={modalState}
            stats={stats}
            user={user}
            gameState={gameState}
            showDefinition={showDefinition}
            definition={definition}
            isLoadingDefinition={isLoadingDefinition}
            toggleDefinition={toggleDefinition}
            resetGame={resetGame}
            countdown={countdown}
            guestGameLimitReached={guestGameLimitReached}
            guestGamesPlayedToday={guestGamesPlayedToday}
            guestDailyGameLimit={GUEST_DAILY_GAME_LIMIT}
          />
        </div>
      )}
      {previewImageUrl && (
        <ImagePreviewModal
          imageUrl={previewImageUrl}
          zoomLevel={displayedZoomLevel}
          zoomOrigin={gameState.zoomOrigin}
          alt={
            gameState.solution
              ? `Expanded generated image for ${gameState.solution.toUpperCase()}`
              : 'Expanded generated image'
          }
          onClose={closeImagePreview}
        />
      )}
      {showAuthModal && (
        <SignUpPromptModal
          onClose={closeAuthModal}
          context={authModalContext}
          guestDailyGameLimit={GUEST_DAILY_GAME_LIMIT}
        />
      )}
      <div className="game-top-container">
        <div
          className={`unlimited-top-main ${
            mobileSideBySide ? 'unlimited-top-main--sideBySide' : ''
          }`}
        >
          <GameImage
            imageLoading={imageLoading}
            imageUrl={gameState.imageUrl}
            zoomLevel={displayedZoomLevel}
            zoomOrigin={gameState.zoomOrigin}
            isChecking={gameState.isChecking}
            disableTransition={disableTransition}
            waitingForFallback={waitingForFallback}
            solution={gameState.solution}
            onLoad={handleImageLoad}
            onError={() => {
              console.error('Image load error for URL:', gameState.imageUrl);
              setImageLoading(false);
              setWaitingForFallback(false);
            }}
            onImageClick={
              imageLoading || gameState.loading || modalState.showModal || isZoomingOut
                ? undefined
                : openImagePreview
            }
          />
          <Grid
            // iOS Safari sometimes fails to recompute internal grid layout when the container
            // width changes due to class toggles (split/stack). Remounting the Grid forces a
            // fresh layout pass and fixes "scrollWidth > offsetWidth" until next reflow.
            key={`${gameId}-${mobileSideBySide ? 'split' : 'stack'}`}
            guesses={gameState.guesses}
            currentGuess={gameState.currentGuess}
            currentRow={gameState.currentRow}
            solution={gameState.solution}
            isTypingEnabled={
              !gameState.gameOver && !gameState.loading && !modalState.showModal
            }
          />
        </div>
        
        <GameControls
          onSkip={handleSkip}
          onInspect={handleInspect}
          onInspectBlocked={!user ? handleInspectBlocked : undefined}
          onZoomOut={handleZoomOut}
          onZoomOutBlocked={!user ? handleZoomOutBlocked : undefined}
          onRiddleOpen={handleRiddlePanelToggle}
          disableSkip={gameState.loading || gameState.gameOver || isSkipping}
          disableInspect={
            gameState.loading ||
            gameState.gameOver ||
            isInspecting ||
            !hasRevealableLetters
          }
          disableRiddle={gameState.loading || gameState.gameOver}
          disableZoomOut={gameState.loading || gameState.gameOver || isZoomingOut}
          isRiddleOpen={showRiddlePanel}
          freeSkipsRemaining={freeSkipsRemaining}
          freeInspectsRemaining={freeInspectsRemaining}
          freeZoomOutsRemaining={freeZoomOutsRemaining}
        />
        <RiddlePanel
          isOpen={showRiddlePanel}
          onClose={handleRiddlePanelToggle}
          onRevealNormal={handleRevealNormalRiddle}
          onRevealEasy={handleRevealEasyRiddle}
          riddleText={riddleText}
          riddles={riddleHistory}
          isLoading={isLoadingRiddle}
          canAffordNormal={canAffordNormalRiddle}
          normalCost={nextRiddleCostNormal}
          canAffordEasy={canAffordEasyRiddle}
          easyCost={nextRiddleCostEasy}
          freeEasyRiddlesRemaining={freeEasyRiddlesRemaining}
          useCount={riddleUseCount}
          errorMessage={riddleError}
        />
      </div>
      <div className="game-bottom-container">
        <Keyboard
          onKeyPress={onKeyPress}
          guesses={gameState.guesses}
          solution={gameState.solution}
          usedLetters={usedLetters}
        />
      </div>
    </div>
  );
};

export default UnlimitedGame;
